/**
 *
 * ProductList
 *
 */

import { useCallback, useMemo, useState } from "react";
import * as PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import messages from "./messages";
import { makeStyles } from "@material-ui/core/styles";
import ProductRow from "containers/TreatmentFlow/ProductRow";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@material-ui/core";
import { hasRole, ROLES } from "utils/userStorage";

const useStyles = makeStyles(theme => ({
  content: {
    borderTop: "1px solid #454546",
  },
}));

export function ProductList({
  products = [],
  onChangeProducts,
  currentProductId = 0,
  onChangeCurrentProductId,
  onEdit,
  onDelete,
}) {
  const classes = useStyles();

  const activeProducts = useMemo(
    () => products.filter(({ active }) => active),
    [products],
  );

  const [wagnerId, setWagnerId] = useState();

  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [productId, setProductId] = useState(0);

  const lastPlannedOrderNumber = useMemo(
    () => {
      const lastPlannedOrder =
        products
          .slice()
          .reverse()
          .find(x => x.status > 1) || {};
      return lastPlannedOrder.order || 0;
    },
    [products],
  );

  const handleClose = useCallback(() => {
    setOpenConfirmation(false);
  }, []);

  const handleOpen = useCallback(() => {
    setOpenConfirmation(true);
  }, []);

  const handleConfirm = useCallback(
    () => {
      const wagnerProductId = products.find(p => !p.active)?.id;

      setWagnerId(wagnerProductId);

      // Activate the inactive product
      onChangeProducts(
        products.map(product => ({
          ...product,
          active: true,
        })),
      );
      if (products.length > 0) {
        onChangeCurrentProductId(products[0].order);
      }
      handleClose();
    },
    [handleClose, onChangeCurrentProductId, onChangeProducts, products],
  );

  const getProductIndex = useCallback(
    orderId => activeProducts.findIndex(({ order }) => order === orderId),
    [activeProducts],
  );

  const handleSkip = useCallback(
    () => {
      const productIndex = getProductIndex(currentProductId);
      const currentActiveProduct = activeProducts[productIndex];

      if (currentActiveProduct.id === wagnerId) {
        onChangeProducts(
          products.map(product => {
            if (product.id === currentActiveProduct.id) {
              return {
                ...product,
                active: false,
              };
            }

            return product;
          }),
        );

        setWagnerId(null);
      }

      const newIndex = Math.min(
        activeProducts.length - 1,
        getProductIndex(currentProductId) + 1,
      );

      onChangeCurrentProductId(activeProducts[newIndex].order);
    },
    [
      activeProducts,
      currentProductId,
      getProductIndex,
      onChangeCurrentProductId,
      wagnerId,
      onChangeProducts,
      products,
    ],
  );

  const handlePrevious = useCallback(
    () => {
      const productIndex = getProductIndex(currentProductId);
      const currentActiveProduct = activeProducts[productIndex];

      if (currentActiveProduct.id === wagnerId) {
        onChangeProducts(
          products.map(product => {
            if (product.id === currentActiveProduct.id) {
              return {
                ...product,
                active: false,
              };
            }

            return product;
          }),
        );

        setWagnerId(null);
      }

      const newIndex = Math.max(0, getProductIndex(currentProductId) - 1);

      onChangeCurrentProductId(activeProducts[newIndex].order);
    },
    [
      activeProducts,
      currentProductId,
      getProductIndex,
      onChangeCurrentProductId,
      onChangeProducts,
      products,
      wagnerId,
    ],
  );

  const isNextProductDisabled = useCallback(
    index => {
      if (index + 1 > products.length - 1) {
        return false;
      }

      return !!products[index + 1].disabled;
    },
    [products],
  );

  const isPreviousProductDisabled = useCallback(
    index => {
      if (index - 1 < 0) {
        return false;
      }

      return !!products[index - 1].disabled;
    },
    [products],
  );

  const renderDialogExtraTreatment = useCallback(
    () => (
      <Dialog
        open={openConfirmation}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          <FormattedMessage {...messages.dialogTitle} />
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            <FormattedMessage {...messages.extraConfirmation} />
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            <FormattedMessage {...messages.cancel} />
          </Button>
          <Button onClick={handleConfirm} color="primary" autoFocus>
            <FormattedMessage {...messages.confirm} />
          </Button>
        </DialogActions>
      </Dialog>
    ),
    [handleClose, handleConfirm, openConfirmation],
  );

  const handleDeleteModalOpen = useCallback(orderId => {
    setDeleteModalOpen(true);
    setProductId(orderId);
  }, []);
  const handleDeleteModalClose = useCallback(() => {
    setDeleteModalOpen(false);
  }, []);

  const handleDeleteModalConfirm = useCallback(
    () => {
      if (productId > 0) {
        onDelete(productId);
        handleDeleteModalClose();
      }
    },
    [handleDeleteModalClose, onDelete, productId],
  );

  const renderDeleteDialog = useCallback(
    () => (
      <Dialog
        open={deleteModalOpen}
        onClose={handleDeleteModalClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          <FormattedMessage {...messages.dialogTitle} />
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            <FormattedMessage {...messages.cancelConfirmation} />
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDeleteModalClose}
            color="primary"
            data-cy="cancel"
          >
            <FormattedMessage {...messages.cancel} />
          </Button>
          <Button
            onClick={handleDeleteModalConfirm}
            color="primary"
            autoFocus
            data-cy="confirm"
          >
            <FormattedMessage {...messages.confirm} />
          </Button>
        </DialogActions>
      </Dialog>
    ),
    [deleteModalOpen, handleDeleteModalClose, handleDeleteModalConfirm],
  );

  if (products.length === 0) {
    return <div />;
  }

  return (
    <div className={classes.content} data-cy="productList">
      {renderDialogExtraTreatment()}
      {renderDeleteDialog()}
      {products.map((product, index) => {
        const isCurrent = product.order === currentProductId;
        const productIndex = getProductIndex(product.order);
        const canPrevious =
          !isPreviousProductDisabled(index) &&
          productIndex > 0 &&
          (hasRole([ROLES.CS, ROLES.Admin, ROLES.KPT]) ||
            product.order > lastPlannedOrderNumber + 1);

        return (
          <ProductRow
            disabled={
              product.disabled ||
              (!hasRole([ROLES.CS, ROLES.Admin, ROLES.KPT]) &&
                product.order <= lastPlannedOrderNumber)
            }
            index={productIndex}
            isCurrent={isCurrent}
            key={index}
            onClickInactive={handleOpen}
            product={product}
            onPrevious={handlePrevious}
            canPrevious={canPrevious}
            onSkip={handleSkip}
            canSkip={
              !isNextProductDisabled(index) && index < products.length - 1
            }
            onEdit={onEdit}
            onDeleteButtonClick={handleDeleteModalOpen}
          />
        );
      })}
    </div>
  );
}

ProductList.propTypes = {
  products: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      fase: PropTypes.string,
      order: PropTypes.number,
      lowerProductId: PropTypes.number,
      lowerProductName: PropTypes.string,
      upperProductId: PropTypes.number,
      upperProductName: PropTypes.string,
      active: PropTypes.bool,
    }),
  ).isRequired,
  onChangeProducts: PropTypes.func.isRequired,
  currentProductId: PropTypes.number,
  onChangeCurrentProductId: PropTypes.func.isRequired,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
};

export default ProductList;
